/**
 * A huddle ring while the Bundle tab is in the background (browser only; the desktop app bounces the Dock instead,
 * notifier.ts). The in-app card and the looping ringtone are IncomingHuddle.tsx; this adds what reaches someone who
 * is looking at another tab or app, like Google Meet:
 *  - a browser notification with Join / Decline (Chrome, Edge and Firefox draw the buttons; Safari only the banner,
 *    whose click opens the huddle). sw/sw.ts handles the clicks and tells the tab (`bundle:huddle-ring`). It has the
 *    tag of the FCM web push for the same ring (apps/api push/payload.ts `huddlePush`), so where both could show, one
 *    replaces the other — and `ringNotifyDecision` only shows it where FCM doesn't (never both for one ring);
 *  - the tab title flashing "Ana is calling you…" while the tab is hidden.
 * Both end with the ring (answered, declined, 30 s, answered elsewhere, huddle over).
 */
import type { NotificationsStatus } from "@bundle/core";

/** The ring's notification tag, the same as the FCM web push one (`huddle-<channelId>`). */
export function ringTag(channelId: string): string {
  return `huddle-${channelId}`;
}

export type RingNotifyContext = {
  permission: NotificationPermission;
  /** This build has FCM web push (push.ts `webPush.availability()`). */
  webPush: boolean;
  /** Notifications are on for this browser (NotificationsViewModel status "on"). */
  pushOn: boolean;
  visible: boolean;
  focused: boolean;
  /** Do not disturb. */
  paused: boolean;
};

/**
 * Whether this tab shows the ring as a browser notification. Not while it is focused (the card is there) or paused.
 * With web push, FCM already shows it when no Bundle tab is visible, so only a visible but unfocused tab (another app
 * in front) shows it here; without web push (unsupported, unconfigured, dev) the tab does it whenever the browser
 * allows notifications, as notifier.ts does for messages.
 */
export function ringNotifyDecision(ctx: RingNotifyContext): boolean {
  if (ctx.permission !== "granted" || ctx.paused || ctx.focused) return false;
  if (ctx.webPush) return ctx.pushOn && ctx.visible;
  return true;
}

/**
 * One tick of the flashing tab title: the flash text, then the page's own title, and so on. The page may retitle
 * itself meanwhile (AppShell's document title), so any title that isn't the flash text is the newest own one.
 */
export function flashTick(current: string, own: string, flash: string): { title: string; own: string } {
  return current === flash ? { title: own, own } : { title: flash, own: current };
}

/** Which notifications banner the browser shows above the shell (NotificationsBanner.tsx). */
export type WebNotifyBanner = "off" | "blocked" | "none";

/**
 * "Turn on notifications" while the browser hasn't been asked, "blocked" once it said no. With web push that is the
 * view model's status; where web push isn't offered (unsupported/unconfigured builds) local notifications still work
 * (notifier.ts), so the browser's own permission decides.
 */
export function webNotifyBanner(status: NotificationsStatus, permission: NotificationPermission | undefined): WebNotifyBanner {
  if (status === "off") return "off";
  if (status === "blocked") return "blocked";
  if (status === "on" || permission === undefined) return "none";
  return permission === "default" ? "off" : permission === "denied" ? "blocked" : "none";
}

// ---- Browser side ----

const FLASH_MS = 1000;

/** Flashes the tab title with `flash` until the returned stop, which puts the page's own title back. */
export function flashTitle(flash: string): () => void {
  let own = document.title;
  const tick = () => {
    const next = flashTick(document.title, own, flash);
    own = next.own;
    document.title = next.title;
  };
  tick();
  const id = setInterval(tick, FLASH_MS);
  return () => {
    clearInterval(id);
    if (document.title === flash) document.title = own;
  };
}

export type RingNotice = {
  title: string;
  body: string;
  channelId: string;
  huddleId: string;
  /** The huddle's call view (`/{ws}/huddle/{channelId}`): Join or a click with no Bundle tab open opens it. */
  huddleUrl: string;
  joinLabel: string;
  declineLabel: string;
};

/** Page notifications (dev servers have no service worker), closed with the ring. */
const pageNotices = new Map<string, Notification>();

async function registration(): Promise<ServiceWorkerRegistration | undefined> {
  return "serviceWorker" in navigator ? navigator.serviceWorker.getRegistration("/") : undefined;
}

/** Shows the ring; silent, since the tab already plays the ringtone. */
export async function showRingNotice(n: RingNotice): Promise<void> {
  const tag = ringTag(n.channelId);
  const options: NotificationOptions = {
    body: n.body,
    tag,
    icon: "/icon-192.png",
    silent: true,
    requireInteraction: true,
    data: { bundleUrl: n.huddleUrl, bundleRing: { huddleId: n.huddleId, channelId: n.channelId } },
  };
  const reg = await registration();
  if (reg !== undefined) {
    // Safari ignores actions; there the click alone opens the huddle (sw.ts).
    const actions = [{ action: "join", title: n.joinLabel }, { action: "decline", title: n.declineLabel }];
    await reg.showNotification(n.title, { ...options, actions } as NotificationOptions);
    return;
  }
  // Page notifications can't have actions: the click brings the tab (and its card) forward.
  const shown = new Notification(n.title, options);
  pageNotices.set(tag, shown);
  shown.addEventListener("click", () => {
    window.focus();
    shown.close();
  });
}

/** Closes the ring's notification, whether this tab or FCM showed it. */
export async function closeRingNotice(channelId: string): Promise<void> {
  const tag = ringTag(channelId);
  pageNotices.get(tag)?.close();
  pageNotices.delete(tag);
  if (!("Notification" in window) || Notification.permission !== "granted") return;
  for (const n of (await (await registration())?.getNotifications({ tag })) ?? []) n.close();
}

/** What sw.ts posts to a tab when a ring notification's Join / Decline is clicked. */
export type RingAction = { type: "bundle:huddle-ring"; action: "join" | "decline"; huddleId: string; channelId: string; url: string };

export function isRingAction(data: unknown): data is RingAction {
  const d = data as Partial<RingAction> | null;
  return d?.type === "bundle:huddle-ring" && (d.action === "join" || d.action === "decline") && typeof d.huddleId === "string" && typeof d.channelId === "string" && typeof d.url === "string";
}
